import { Button } from 'app/components/ui/Button'
import { Card } from 'app/components/ui/Card'
import { Modal } from 'app/components/ui/Modal'
import { Text } from 'app/components/ui/Text'
import { formatApiIsoDate } from 'app/utils/date'
import { QuotationRequest } from 'EstraSuppliers'
import React, { useState } from 'react'
import styled from 'styled-components'
import { UploadQuotation } from '../UploadQuotation'

interface Props {
  quotation: QuotationRequest
  refetchQuotation: () => void
}

export const QuotationBox: React.FC<Props> = ({
  quotation: {
    id,
    status,
    quotation_status,
    quotation_url,
    completed_quotation_url,
    rejected_notes,
    uploaded_at,
  },
  refetchQuotation,
}) => {
  const [showUploadModal, setShowUploadModal] = useState(false)

  // status, // status from emma-contacts
  // quotation_status // status from emma-quotations
  const extendedStatus = quotation_status || status
  const isPending = [
    'approved',
    'sent',
    'new',
    'blocked',
    'in_progress',
  ].includes(extendedStatus)
  const showUploadBox = quotation_url == null

  // user can update quotation only if is its quotation.status is not in_progress and following status (approved, blocked, rejected, completed)
  // for rejected status user will still be able to upload a new quoation, but only by inteacting with Alert Rejected
  const canUpdateQuotation =
    quotation_url &&
    !['in_progress', 'blocked', 'rejected', 'completed'].includes(
      extendedStatus
    )

  const cardBgColor = () => {
    if (extendedStatus === 'rejected') return 'rosso'
    if (extendedStatus === 'canceled') return 'grigioestra'
    if (isPending || extendedStatus === 'pending') return 'aranciogiallo'
    if (extendedStatus === 'completed') return 'verde'
    return 'blu'
  }

  return (
    <>
      <QuotationBoxWrapper>
        <QuotationCard shadow={showUploadBox} bgGradient={cardBgColor()}>
          {/* ALERT REJECTED */}
          {status === 'rejected' && (
            <>
              <Text color='white' variant='s18' mb={1.5}>
                Il tuo preventivo è stato rifiutato
                {rejected_notes && (
                  <span> con la seguente motivazione: {rejected_notes}.</span>
                )}
              </Text>
              <Button onClick={() => setShowUploadModal(true)}>
                Aggiorna il preventivo
              </Button>
            </>
          )}

          {/* ALERT PENDING */}
          {extendedStatus === 'pending' && (
            <>
              <Text color='white' variant='s18' mb={1.5}>
                Grazie per avere inviato il tuo preventivo in data{' '}
                {formatApiIsoDate(
                  uploaded_at as string,
                  true,
                  false,
                  ' alle ore'
                )}
                {'\n'} {'\n'}
                Riceverai una notifica via email non appena avremo visionato il
                documento
              </Text>
              <Button
                target='_blank'
                rel='noopener noreferrer'
                href={quotation_url}
              >
                Vedi preventivo
              </Button>
            </>
          )}

          {/* ALERT In gestione */}
          {isPending && (
            <>
              <Text color='white' variant='s18' mb={1.5}>
                Il tuo preventivo, inviato in data{' '}
                {formatApiIsoDate(
                  uploaded_at as string,
                  true,
                  false,
                  ' alle ore'
                )}
                , è in gestione alla nostra amministrazione.{'\n'} {'\n'}
                Riceverai una notifica via email al completamento.
              </Text>
              <Button
                target='_blank'
                rel='noopener noreferrer'
                href={quotation_url}
              >
                Vedi preventivo
              </Button>
            </>
          )}

          {/* ALERT APPROVED */}
          {extendedStatus === 'completed' && (
            <>
              <Text color='white' variant='s18' mb={1.5}>
                Il tuo preventivo, inviato in data{' '}
                {formatApiIsoDate(
                  uploaded_at as string,
                  true,
                  false,
                  ' alle ore'
                )}
                , è stato approvato.
              </Text>
              <Button
                target='_blank'
                rel='noopener noreferrer'
                href={completed_quotation_url}
              >
                Vedi preventivo
              </Button>
            </>
          )}

          {/* ALERT REJECTED (from emma-quotations) */}
          {quotation_status === 'rejected' && (
            <>
              <Text color='white' variant='s18' mb={1}>
                Ci dispiace, ma il tuo preventivo è stato annullato.
              </Text>
            </>
          )}

          {/* ALERT CANCELED (from emma-quotations) */}
          {quotation_status === 'canceled' && (
            <>
              <Text color='white' variant='s18' mb={1}>
                Il tuo preventivo è stato annullato.
              </Text>
            </>
          )}

          {/* BOX UPLOAD */}
          {showUploadBox && (
            <UploadQuotation
              quotationId={id.toString()}
              onSuccessUpload={() => {
                refetchQuotation()
              }}
            />
          )}
        </QuotationCard>
        {/* AGGIORNAMENTO PREVENTIVO */}
        {!showUploadBox && canUpdateQuotation ? (
          <UpdateQuotationWrapper>
            <Text variant='s12' isItalic mb={1}>
              Se vuoi, puoi sostituire il preventivo caricato in precedenza.
              {'\n'}
              Il preventivo sarà soggetto a nuova approvazione.
            </Text>
            <Button onClick={() => setShowUploadModal(true)}>
              <UpdateQuotationLabelMobile>AGGIORNA</UpdateQuotationLabelMobile>
              <UpdateQuotationLabelDesktop>
                AGGIORNAMENTO PREVENTIVO
              </UpdateQuotationLabelDesktop>
            </Button>
          </UpdateQuotationWrapper>
        ) : null}
      </QuotationBoxWrapper>
      {showUploadModal && (
        <Modal
          onCloseRequest={() => setShowUploadModal(false)}
          title='Aggiornamento Preventivo'
          size='md'
        >
          <UploadQuotation
            quotationId={id.toString()}
            isInModal
            onSuccessUpload={() => {
              setShowUploadModal(false)
              refetchQuotation()
            }}
          />
        </Modal>
      )}
    </>
  )
}

const QuotationBoxWrapper = styled.div`
  margin-bottom: 4rem;
`

const QuotationCard = styled(Card)`
  margin-bottom: 1rem;
`

const UpdateQuotationWrapper = styled(Text)`
  margin-left: 1rem;
`
const UpdateQuotationLabelDesktop = styled.span`
  @media (max-width: ${({ theme }) => theme.bp.desktop}) {
    display: none;
  }
`
const UpdateQuotationLabelMobile = styled(Button)`
  @media (min-width: ${({ theme }) => theme.bp.desktop}) {
    display: none;
  }
`
